import { useContext, useEffect, useState } from "react";
import TRANSLATIONS from "../modules/dataTextos.json";
import { store } from "../components/Store";
import { AVAILABLE_LANGUAGES_ARRAY, AVAILABLE_LANGUAGES } from "../config/constants";
import { useLanguage } from "./use-language";

/**
 * Hook personalizado para gestionar las traducciones en la aplicación.
 *
 * @param {Object} options - Opciones para el hook.
 * @param {string} options.mainSectionName - Nombre de la sección principal de las traducciones.
 * @param {string} options.language - Idioma específico para las traducciones (opcional).
 * @returns Objeto con la propiedad fullData y la función getText
 */
export const useTranslationsById = ({ mainSectionName, language }) => {
	const { state } = useContext(store);
	const appLanguageCode = useLanguage();
	const mainSectionLanguage = language;
	const mainSection = Array.isArray(TRANSLATIONS?.[mainSectionName]) ? TRANSLATIONS?.[mainSectionName] : [];

	/**
	 * Reemplaza los marcadores de posición en el texto con los valores proporcionados en los parámetros.
	 *
	 * @param {Object} options - Opciones para el reemplazo de texto.
	 * @param {string} options.text - Texto que contiene marcadores de posición a reemplazar.
	 * @param {Object} options.params - Objeto con clave-valor de parámetros para reemplazar en el texto.
	 *   Los marcadores de posición en el texto deben tener el formato: {{nombreDelParametro}}.
	 *   El objeto `params` debe contener parámetros con nombres coincidentes con los marcadores de posición en el texto.
	 *   Si se encuentran coincidencias, se reemplazará cada marcador de posición por el valor correspondiente en `params`.
	 * @returns {string} Texto con los marcadores de posición reemplazados.
	 */
	const replaceTextWithParams = ({ text, params }) => {
		if (typeof params !== "object" || Object.keys(params).length <= 0) {
			return text;
		}

		let _finalText = text;
		Object.entries(params).forEach(([key, value]) => {
			_finalText = _finalText?.replace(new RegExp(`{{${key}}}`, "g"), value);
		});

		return _finalText;
	};

	/**
	 * Obtiene el texto traducido según el ID proporcionado y el idioma actual.
	 *
	 * @param {Object} options - Opciones para la obtención del texto.
	 * @param {string} options.id - ID del texto que se quiere obtener.
	 * @param {string} [options.defaultText] - Texto predeterminado si no se encuentra la traducción.
	 * @param {string} [options.language] - Idioma específico para la traducción (opcional).
	 * @param {Object} [options.params] - Objeto con clave-valor de parámetros para reemplazar en el texto.
	 *   Los marcadores de posición en el texto deben tener el formato: {{nombreDelParametro}}.
	 *   El objeto `params` debe contener parámetros con nombres coincidentes con los marcadores de posición en el texto.
	 *   Si se encuentran coincidencias, se reemplazará cada marcador de posición por el valor correspondiente en `params`.
	 * @returns {string} Texto traducido.
	 */
	const getText = ({ id, defaultText, language, params }) => {
		const text = mainSection.find((_text) => _text?.id?.toLowerCase() === id?.toLowerCase());

		/**
		 * Determina el idioma final a utilizar para la traducción.
		 *
		 * @returns {string} Idioma final.
		 */
		const finalLanguage = () => {
			if (language && AVAILABLE_LANGUAGES_ARRAY.includes(language)) return language;
			if (mainSectionLanguage && AVAILABLE_LANGUAGES_ARRAY.includes(mainSectionLanguage))
				return mainSectionLanguage;
			if (appLanguageCode && AVAILABLE_LANGUAGES_ARRAY.includes(appLanguageCode))
				return appLanguageCode;
			return AVAILABLE_LANGUAGES.es;
		};

		const lang = finalLanguage();

		if (text) {
			const _finalText = text?.[lang];
			return replaceTextWithParams({ text: _finalText, params: params || {} });
		}
		return defaultText || "";
	};


	return {
		/**
		 * Arreglo con todos los textos de la sección solicitada.
		 * @type {Array<{id:string, en:string, es:string}>}
		 */
		fullData: mainSection,
		getText,
	};
};
